import { isNil } from 'lodash'

// =============================================================================
// Pure helpers for the LLM settings UI (AVR-527). Every capability decision
// comes from the BE model catalog (GET /prompts/llm/catalog) — nothing about
// specific models is hardcoded here beyond the 'none' effort sentinel the
// catalog's `when_effort_none` flag refers to.
//
// Override blobs are SPARSE and field presence is meaningful (same contract
// as the BE): a field ABSENT from the override means "inherit" (delegate to
// agent level / baseline), a field explicitly null means "cleared" (suppress
// inherited values — the runtime falls back to the deployment default), and
// a field with a value is set. Neither absent nor null means "keep previous".
// =============================================================================

// node_id addressing the agent-level settings target on the /prompts/llm
// endpoints (everything else is a subagent node id).
export const AGENT_LEVEL_NODE_ID = '__agent__'

export const EFFORT_NONE = 'none'

export const LLM_FIELDS = ['model', 'temperature', 'reasoningEffort']

export const findCatalogModel = (models, modelId) =>
  (models || []).find((entry) => entry.id === modelId) || null

// Display label for a model id; unknown ids (env fallback, removed model)
// fall back to the raw id.
export const modelLabel = (models, modelId) => findCatalogModel(models, modelId)?.label || modelId

// Presence-preserving copy of an override blob: only the LLM fields actually
// present survive, with explicit nulls kept ("cleared"). null/undefined blobs
// (no override at all) come back as {}.
export const sparseLlmOverride = (blob) => {
  const out = {}
  LLM_FIELDS.forEach((field) => {
    if (blob && field in blob && blob[field] !== undefined) {
      out[field] = blob[field]
    }
  })
  return out
}

const SNAKE_KEY_BY_FIELD = {
  model: 'model',
  temperature: 'temperature',
  reasoningEffort: 'reasoning_effort',
}

// Version `content` is the raw JSON string of the override at that version,
// sparse with snake_case keys ({"model", "temperature", "reasoning_effort"});
// null content marks a reset-to-baseline entry. Presence semantics carry
// over: an absent field was inheriting, a present-null field was cleared.
export const parseLlmVersionContent = (content) => {
  if (!content) {
    return null
  }
  try {
    const parsed = JSON.parse(content)
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    const out = {}
    LLM_FIELDS.forEach((field) => {
      const key = SNAKE_KEY_BY_FIELD[field]
      if (key in parsed) {
        out[field] = parsed[key] ?? null
      }
    })
    return out
  } catch (parseError) {
    return null
  }
}

// One-line human summary of an override for version rows.
export const formatLlmOverride = (override, models) => {
  if (!override) {
    return 'Reset to baseline'
  }
  const describe = (field, label) => {
    if (!(field in override)) {
      return `${label}: inherit`
    }
    if (isNil(override[field])) {
      return `${label}: cleared`
    }
    return `${label}: ${field === 'model' ? modelLabel(models, override.model) : override[field]}`
  }
  return [
    describe('model', 'model'),
    describe('temperature', 'temperature'),
    describe('reasoningEffort', 'reasoning effort'),
  ].join(' · ')
}

// Whether the temperature control renders/accepts input for a model at a given
// effective reasoning effort. `supportsTemperature: 'no'` hides the control
// entirely (the model's `notes` explain why); `'when_effort_none'` shows it but
// only enables it while the effective effort is 'none'.
export const temperatureAvailability = (modelEntry, effectiveEffort) => {
  if (!modelEntry || modelEntry.supportsTemperature === 'no') {
    return { visible: false, enabled: false }
  }
  if (modelEntry.supportsTemperature === 'when_effort_none') {
    return { visible: true, enabled: effectiveEffort === EFFORT_NONE }
  }
  return { visible: true, enabled: true }
}
